/* 
 * Europarl ENG Main Navigation
 *
 * This support the opening and closing in the header menu section of the ENG Europarl websbite 
 * - Main Navigation
 * - Other Websites
 * - Main Navigation in "compact" mode
 * - Other Websites in "compact" mode
 * 
 * The navigation dropdown are built using the ":focus-within" css pseudo class
 *
 * Principle is unusual but works great : 
 * - a div contains the menu element (hidden) and the link to open it (visible)
 * - when any children of the div have the focus, the pseudo-class is active and 
 *   the menu dropdown is visible
 * - when the focus is released or placed elsewhere, the menu is closed
 * 
 * - Clicking / Taping on an element place the focus on it, as does tabbing to it.
 * - Clicking / Taping elsewhere or tabbing out if it remove the focus from the element.
 * - Moving the focus to another element within the same _focus-within container_ 
 *   keeps it active (thus open/visible)
 * 
 * Main issue with this approach are :
 *  - Clicking/Tapping multiple times on an element doesn't toggle the focus 
 *  (its added on first tap then don't change) -> no way to close the menu by taping again
 *  - Simply removing the focus on an element with Javascript while enough to close an element is
 *  problematic when there is submenu (as the parent will close as well).
 *  Workaround is to move the focus passed that element to an anchor point injected after it 
 *  (which is still in the parent focus-within scope).
 *
 * DOM structure :
 
<nav class="navigation-menu nav-main" aria-label="Main navigation">
  <div class="menu-container">
    <div class="focusable">
      <a href="#" class="menu-icon"><span class="nav-icon"></span></a>
      <ul class="menu-level-0">
        <li class="level-0 current has-menu-dropdown">
          <a class="icon-arrow-dropdown" href="#" aria-current="true" aria-haspopup="true" aria-labelledby="first-entry-menu">
            <span class="content">
              <span class="label">Headlines</span>
              <span class="current-element"></span>
            </span>
          </a>
          <ul class="menu-level-1" id="first-entry-menu" aria-label="View submenu">
            <li class="level-1">
              <a class="current" href="#" aria-current="true">
                <span class="content">
                  <span class="sr-only">Go to the page</span>
                  <span class="label">Homepage</span>
                </span>
              </a>
            </li>
          
            <!-- here the resting anchor will be injected by js
            <li>
              <a href="#" tabindex="-1" class="focusRestingAnchor"></a>
            </li>            
            -->
            
            ... 

          </ul>

          ...

        </li>
        ...

      </ul>
    </div>
  </div>
</nav>

 * 
 * Javascript will:
 *  - Target all `.navigationFocusWithin` div
 *  - Attach a listener to the focus-within pseudo class to track opening / close
 *  - Set and toggle aria-expended accordingly
 *  - Improve keyboard navigation
 *  - Move a "current" class on the currently open menu element ?
 *
 * Notes : Using the resting anchor is prefered to simple "blur of focus" (which would work in simpler scenarios) 
 * because of the submenu. Blurring would close main menu when closing sub ones.
 * If it becomes an issue, we'll have to complexify a little bit and when closing a submenu, 
 * move the focus on the upper level header link, which might seems weird as well...
 * 
 * Polyfill : ie11 doensnt support focus within, a polyfill is necessary
 *  
 * @author Edwin Joassart <edwin.joassart@ext.europarl.europa.eu>
 * @copyright Europarl (c) 2021
 * 
 */
class ENGMainNavigation {
  constructor(menuElement) {
    if (!menuElement) return;

    // get elments
    this.menuElement = menuElement;
    this.button = document.querySelector('.menu-icon');

    // inject resting anchor after the menu element

    this.focusRestingAnchor = document.createElement('a');
    this.focusRestingAnchor.classList.add('focusRestingAnchor');
    this.focusRestingAnchor.setAttribute('tabindex', '-1');
    this.focusRestingAnchor.setAttribute('aria-hidden', 'true');

    // if part of a list we need to put the anchor into a list element
    if (this.menuElement.tagName === 'LI') {
      this.focusRestingContainer = document.createElement('li');
      this.focusRestingContainer.classList.add('level-0');
      this.focusRestingContainer.classList.add('resting'); // ie11 doesn't support more than 1 class per add
      this.focusRestingContainer.height = 0;
      this.focusRestingContainer.width = 0;
      // this.focusRestingContainer.style =
      //   'height: 0px; width: 0px; color: transparent';
      this.focusRestingContainer.setAttribute('tabindex', '-1');
      this.focusRestingContainer.setAttribute('aria-hidden', 'true');
      this.focusRestingContainer.appendChild(this.focusRestingAnchor); // eslint-disable-line unicorn/prefer-node-append
      this.menuElement.after(this.focusRestingContainer);
    } else {
      this.menuElement.after(this.focusRestingAnchor);
    }

    // get the menu opening link (it's the first one)
    this.link = menuElement.querySelector('a');

    // are we the otherwebsite link ?
    this.isOtherWebsite = this.link.closest('ul')?.id === 'other-website-menu';

    // add aria-expended to the link
    this.link.setAttribute('aria-expanded', 'false');

    // attach listener to the link to track status

    /* WORKAROUND : we had to decouple `mousedown` and `mouseup` from `click` 
    and add this weird delegated event flag.

    Click event fire only if mousedown is followed by mouseup on the *same* element
    As in compact mode menu is acting as a accordion (by CSS) and browser apply CSS on mousedown,
    in some edgecase we miss the mouseup and so miss the click entirely.

    To avoid that, we need to use mousedown, but it creates another issue where browser will
    refocus the element on mouseup. So we can't close the menu with js (as closing means we 
    programatically move the focus away from the element)

    So workaround is to defer moving the focus, by raising a flag `shouldMoveFocusOnClickRelease` on `mousedown`
    and move the focus on `mouseup`.
    */

    // track clicks
    document.addEventListener(
      'mousedown',
      this.documentClickEventHandler.bind(this)
    );

    document.addEventListener(
      'mouseup',
      this.linkClickReleaseEventHandler.bind(this)
    );

    this.shouldMoveFocusOnClickRelease = false;

    // Attach hover event handler
    if (!document.querySelector('.nav-top').contains(this.link)) {
      menuElement.addEventListener('mouseenter', this.focusOnHover.bind(this));
      menuElement.addEventListener('mouseleave', this.blurOnLeave.bind(this));
    }
  }

  focusOnHover(_event) {
    if (window.getComputedStyle(this.button).display !== 'none') return;
    this.linkOpen();
    this.link.focus();
  }

  blurOnLeave(_event) {
    if (window.getComputedStyle(this.button).display !== 'none') return;
    this.linkClose({ noBlur: true });
    document.activeElement.blur();
  }

  linkOpen() {
    // css will handle opening for us
    this.link.setAttribute('aria-expanded', 'true');

    // add the grey background on the link and the list and remove from all others
    // unless we're on the compact menu button
    document
      .querySelectorAll('.backgrey')
      .forEach((element) => element.classList.remove('backgrey'));

    if (!this.link.classList.contains('menu-icon')) {
      this.link.classList.add('backgrey');
      this.menuElement.querySelector('ul').classList.add('backgrey');
    }

    if (
      this.link.classList.contains('menu-icon') &&
      document.querySelector('.back-overlay')
    ) {
      document.querySelector('.back-overlay').style.display = 'block';
    }

    // add active class on link
    this.link.classList.add('active');
  }

  linkClose(options) {
    this.link.setAttribute('aria-expanded', 'false');

    // remove the grey backgroun and active on the link and the list
    this.link.classList.remove('backgrey');

    // remove class active
    this.link.classList.remove('active');

    if (
      this.link.classList.contains('menu-icon') &&
      document.querySelector('.back-overlay')
    ) {
      document.querySelector('.back-overlay').style.display = 'none';
    }

    // defer focus setting to mouseup event handler (cf notes in constructor)
    if (!options?.noBlur) this.shouldMoveFocusOnClickRelease = true;
  }

  // /** move focus on mouseup if flag is set (cf notes in constructor) */
  linkClickReleaseEventHandler(_event) {
    // _event.preventDefault() //prevent mouseup to put back the focus on the element
    if (!this.shouldMoveFocusOnClickRelease) return;
    this.focusRestingAnchor.focus();
    this.shouldMoveFocusOnClickRelease = false;
  }

  /**
   * open / close for compact menu
   * nb : main menu is opened/closed with hover
   */
  documentClickEventHandler(_event) {
    // only act if compact mode
    if (
      (!this.button || window.getComputedStyle(this.button).display) === 'none' &&
      !this.isOtherWebsite
    )
      return;

    // open/close when clicking a link
    if (
      _event.target.closest('a') === this.link ||
      _event.target === this.link
    ) {
      if (this.link.getAttribute('aria-expanded') === 'true') {
        // open -> close
        this.linkClose();
        return;
      }

      // close -> open
      this.linkOpen();
      return;
    }

    // close on "click outside" unless it's a parent of clicked link
    // aka "close everything else"
    if (
      this.link.closest('li, nav') && !this.link.closest('li, nav').contains(_event.target) &&
      this.link.getAttribute('aria-expanded') === 'true'
    )
      this.linkClose({ noBlur: true });
  }

  static init() {
    // load for compact other websites
    document
      .querySelectorAll('div.menu-container')
      .forEach((menuElement) => new ENGMainNavigation(menuElement));

    // load for all menu dropdown content (both other webistes and main nav)
    document
      .querySelectorAll('li.has-menu-dropdown')
      .forEach((menuElement) => new ENGMainNavigation(menuElement));

    console.info('ENG Main Navigation : 0.4');
  }
}

export default ENGMainNavigation;
